import type { TrendPoint } from "../types";
import { fmt } from "../utils/format";

interface SparklineBarsProps {
  data: TrendPoint[];
  /** Tailwind bg class for the active (latest) bar, e.g. "bg-primary" */
  bgActive?: string;
  /** Height class, e.g. "h-28" or "h-16" */
  height?: string;
}

/**
 * Renders N vertical bars from trend data (oldest → newest left to right).
 * The rightmost (latest) bar gets the accent color; others are gray.
 */
export default function SparklineBars({
  data,
  bgActive = "bg-primary",
  height = "h-28",
}: SparklineBarsProps) {
  // Data comes newest-first from the API; reverse to oldest→newest
  const pts = [...data].reverse();
  const maxVal = Math.max(...pts.map((p) => Math.abs(p.value ?? 0)), 1);

  return (
    <div className={`${height} mt-2 flex items-end gap-1.5`}>
      {pts.map((p, i) => {
        const h = Math.max(8, (Math.abs(p.value ?? 0) / maxVal) * 100);
        const isLatest = i === pts.length - 1;
        const bg = isLatest ? bgActive : "bg-card-border";
        const glow = isLatest
          ? "shadow-[0_0_10px_rgba(59,130,246,0.3)]"
          : "";
        const qLabel = p.period_end ? p.period_end.slice(0, 7) : "";

        return (
          <div
            key={p.period_end ?? i}
            className={`sparkline-bar flex-1 rounded-t-sm ${bg} ${glow} hover:opacity-80 relative group/bar cursor-default`}
            style={{ height: `${h}%` }}
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#1e1e1e] text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/bar:opacity-100 transition-opacity whitespace-nowrap shadow-lg border border-card-border z-10">
              <span className="font-mono font-bold">
                {fmt(p.value, p.unit)}
              </span>
              <br />
              <span className="text-text-secondary">{qLabel}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
